import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Dropdown } from "antd";
import Search from "antd/es/transfer/search";
import { toast } from "react-toastify";
import ContentLayout from "../../layout/ContentLayout";
import TableSection from "../../components/tables/TableSection";
import DeleteModal from "../../components/modals/DeleteModal";
import ToggleSwitch from "../../components/elements/toggleSwitch/ToggleSwitch";
import Spinner from "../../components/elements/Spinner/Spinner";
import quizService from "../../api/quiz-api";
import { useDebounce } from "../../hooks/useDebounce";
import { formatDate } from "../../utils/formatDate";
import { DISPLAY_PAGE_OPTIONS, TARGET_USER_GROUP_OPTIONS } from "../../utils/announcement.util";
import { QUIZ_FILTER_OPTIONS, formatQuizStatusAsPill } from "../../utils/quiz.util";
import styles from "../../styles/users.module.scss";

export default function KvizoviPage() {
    const navigate = useNavigate();
    const location = useLocation();

    const queryParams = new URLSearchParams(location.search);
    const initialPage = parseInt(queryParams.get('page')) || 1;

    const [data, setData] = useState([]);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [filter, setFilter] = useState({});
    const [loading, setLoading] = useState(true);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [loadingDelete, setLoadingDelete] = useState(false);

    const debouncedSearch = useDebounce(search, 500);

    const loadQuizzes = useCallback(async () => {
        setLoading(true);
        try {
            const response = await quizService.getAll(currentPage, 10, filter.status, debouncedSearch);
            if (!response.success) {
                toast.error(response.message || 'Greška pri učitavanju kvizova');
                setData([]);
                setTotalPages(1);
                setTotalItems(0);
                return;
            }
            setData(response.data.items || []);
            setTotalPages(response.data.totalPages || 1);
            setTotalItems(response.data.totalItems || 0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, filter.status, debouncedSearch]);

    useEffect(() => {
        loadQuizzes();
    }, [loadQuizzes]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        const params = new URLSearchParams(location.search);
        params.set('page', page);
        navigate({ search: params.toString() }, { replace: true });
    };

    const onStatusToggleClick = async (id) => {
        try {
            const response = await quizService.toggleStatus(id);
            if (!response.success) {
                toast.error(response.message || 'Greška pri promeni statusa');
                return;
            }
            toast.success('Status je uspešno promenjen');
            loadQuizzes();
        } catch (error) {
            toast.error('Greška pri promeni statusa');
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setLoadingDelete(true);
        try {
            const response = await quizService.delete(deleteTarget._id);
            if (!response.success) {
                toast.error(response.message || 'Greška pri brisanju kviza');
                return;
            }
            toast.success('Kviz je uspešno obrisan');
            setDeleteTarget(null);
            loadQuizzes();
        } finally {
            setLoadingDelete(false);
        }
    };

    const columns = [
        {
            key: 'name',
            label: 'Naziv',
            render: (val) => val,
        },
        {
            key: 'status',
            label: 'Status',
            render: (val) => formatQuizStatusAsPill(val),
        },
        {
            key: 'displayInPage',
            label: 'Stranica',
            render: (val) => {
                const option = DISPLAY_PAGE_OPTIONS.find((opt) => opt.value === val) ||
                    (val === 'app-open-page' ? { label: 'Otvaranje aplikacije' } : null);
                return option ? option.label : 'Nepoznata stranica';
            },
        },
        {
            key: 'targetUserGroup',
            label: 'Target korisnika',
            render: (val) => {
                const option = TARGET_USER_GROUP_OPTIONS.find((opt) => opt.value === val);
                return option ? option.label : 'Nepoznat target';
            },
        },
        {
            key: 'questions',
            label: 'Broj pitanja',
            render: (val) => (val || []).length,
        },
        {
            key: 'startDate',
            label: 'Start datum',
            render: (val) => formatDate(val),
        },
        {
            key: 'endDate',
            label: 'Datum završetka',
            render: (val) => formatDate(val),
        },
        {
            key: 'openingCounter',
            label: 'Broj prikaza',
            render: (val) => val || 0,
        },
    ];

    const actions = [
        {
            key: 'header',
            label: 'Opcije',
            disabled: true,
            className: 'dropdown-header',
        },
        {
            key: 'status-toggle',
            label: (row) => (
                <div onClick={(e) => e.stopPropagation()}>
                    <ToggleSwitch
                        checked={row.status === 'active'}
                        onChange={() => onStatusToggleClick(row._id)}
                        size="md"
                        label={row.status === 'active' ? 'Deaktiviraj' : 'Aktiviraj'}
                    />
                </div>
            ),
            onClick: () => {},
            className: 'toggle-switch-item',
        },
        {
            key: 'results',
            label: 'Rezultati',
            icon: <img src="/assets/icons/view-icon.svg" alt="Rezultati" />,
            onClick: (quiz) => navigate(`/kvizovi/rezultati/${quiz._id}`),
        },
        {
            key: 'edit',
            label: 'Izmeni kviz',
            icon: <img src="/assets/icons/edit-icon.svg" alt="Izmeni" />,
            onClick: (quiz) => navigate(`/kvizovi/izmeni/${quiz._id}`),
        },
        {
            key: 'delete',
            label: 'Obriši kviz',
            icon: <img src="/assets/icons/delete.svg" alt="Delete" />,
            onClick: (quiz) => setDeleteTarget(quiz),
            className: 'delete',
            danger: true,
        },
    ];

    return (
        <>
            <ContentLayout>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h2 className="title">Kvizovi</h2>
                    <Button className="btn-primary" type="primary" onClick={() => navigate('/kvizovi/dodaj')}>
                        Dodaj kviz
                    </Button>
                </div>

                <div className={`${styles.wrapper} users_wrapper`}>
                    <div className={styles.filters}>
                        <div className={styles.filters__left}>
                            <Search
                                placeholder="Pretraži"
                                className="search-input"
                                allowClear
                                enterButton
                                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                                value={search}
                            />
                            <Dropdown
                                menu={{
                                    items: QUIZ_FILTER_OPTIONS.map((option) => ({
                                        key: option.value,
                                        label: option.label,
                                        onClick: () => {
                                            setFilter((prev) => ({ ...prev, status: option.value }));
                                            setCurrentPage(1);
                                        },
                                    })),
                                }}
                                trigger={['click']}
                                placement="bottomLeft"
                                className="btn-filter-container"
                            >
                                <Button className={`btn-tertiary btn-filter ${filter.status ? 'active' : ''}`}>
                                    {filter.status
                                        ? `Status: ${QUIZ_FILTER_OPTIONS.find((option) => option.value === filter.status)?.label}`
                                        : 'Status'}
                                    {filter.status && (
                                        <img
                                            src="/assets/icons/x-gray.svg"
                                            title="Ukloni filter status"
                                            style={{ width: '10px', height: '10px', marginRight: '5px', cursor: 'pointer' }}
                                            alt="Ukloni filter"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setFilter((prev) => ({ ...prev, status: null }));
                                                setCurrentPage(1);
                                            }}
                                        />
                                    )}
                                </Button>
                            </Dropdown>
                        </div>
                    </div>

                    {loading ? (
                        <Spinner size="lg" />
                    ) : (
                        <TableSection
                            columns={columns}
                            actions={actions}
                            data={data}
                            emptyState={{
                                icon: '/assets/icons/no-annoucement.svg',
                                title: 'Trenutno nema kvizova',
                                description: 'Kreirajte prvi kviz',
                            }}
                            showPagination={data.length > 0}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={totalItems}
                            onPageChange={handlePageChange}
                            onSearch={() => {}}
                            searchPlaceholder="Pretraži"
                        />
                    )}
                </div>
            </ContentLayout>

            <DeleteModal
                visible={!!deleteTarget}
                onDelete={handleDelete}
                onCancel={() => setDeleteTarget(null)}
                loading={loadingDelete}
                title="Brisanje kviza"
                message={`Da li ste sigurni da želite da obrišete kviz "${deleteTarget?.name}"?`}
                buttonText="Obriši kviz"
                cancelText="Odustani"
            />
        </>
    );
}
